import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  LockKeyhole, LogOut, Trash2, Pencil, Eye, EyeOff, Check, X, Loader2,
  Image as ImageIcon, Video as VideoIcon, UploadCloud, ArrowLeft, Save,
} from "lucide-react";
import { api, setAdminToken, formatApiErrorDetail, mediaUrl, formatDate } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("yes_admin_token"));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const [form, setForm] = useState({ title: "", event_date: "", description: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadMsg, setUploadMsg] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const fileInputRef = useRef(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchPosts = async () => {
    setLoadingPosts(true);
    try {
      const { data } = await api.get("/admin/media", authHeaders);
      setPosts(data);
    } catch {
      setPosts([]);
    }
    setLoadingPosts(false);
  };

  useEffect(() => {
    if (token) fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await api.post("/admin/login", { email, password });
      setAdminToken(data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setLoggingIn(false);
  };

  const logout = () => {
    setAdminToken(null);
    setToken(null);
    setPosts([]);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      setUploadMsg({ ok: false, text: "Choose a photo or video file first." });
      return;
    }
    setUploading(true);
    setProgress(0);
    setUploadMsg(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    fd.append("event_date", form.event_date);
    fd.append("description", form.description);
    try {
      const { data } = await api.post("/admin/media", fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)),
      });
      setPosts((p) => [data, ...p]);
      setForm({ title: "", event_date: "", description: "" });
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadMsg({ ok: true, text: "Uploaded as a draft — publish it when you're ready." });
    } catch (err) {
      setUploadMsg({ ok: false, text: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    }
    setUploading(false);
  };

  const togglePublish = async (post) => {
    const { data } = await api.put(
      `/admin/media/${post.id}`,
      { published: !post.published },
      authHeaders
    );
    setPosts((p) => p.map((x) => (x.id === post.id ? data : x)));
  };

  const startEdit = (post) => {
    setEditingId(post.id);
    setEditDraft({ title: post.title, event_date: post.event_date || "", description: post.description || "" });
  };

  const saveEdit = async (post) => {
    const { data } = await api.put(`/admin/media/${post.id}`, editDraft, authHeaders);
    setPosts((p) => p.map((x) => (x.id === post.id ? data : x)));
    setEditingId(null);
  };

  const remove = async (post) => {
    if (!window.confirm(`Remove "${post.title}" from the site? This cannot be undone.`)) return;
    await api.delete(`/admin/media/${post.id}`, authHeaders);
    setPosts((p) => p.filter((x) => x.id !== post.id));
  };

  /* ---------------- Login view ---------------- */
  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6 py-16 bg-lavender-50">
        <div className="w-full max-w-md">
          <Link to="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-purple-800 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <form onSubmit={login} className="bg-white border border-purple-100 rounded-3xl p-8 sm:p-10 shadow-2xl shadow-purple-200/60" data-testid="admin-login-form">
            <LogoMark className="w-12 h-12" />
            <h1 className="font-display text-2xl font-extrabold text-slate-900 mt-5 flex items-center gap-2.5">
              <LockKeyhole className="w-5 h-5 text-purple-700" /> Admin Portal
            </h1>
            <p className="text-slate-500 text-sm mt-2">Sign in to manage events &amp; media uploads.</p>
            <div className="mt-7 space-y-5">
              <div>
                <label className="field-label" htmlFor="admin-email">Email</label>
                <input
                  id="admin-email"
                  type="email"
                  className="field"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                  placeholder="admin@email.com"
                />
              </div>
              <div>
                <label className="field-label" htmlFor="admin-password">Password</label>
                <input
                  id="admin-password"
                  type="password"
                  className="field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                  placeholder="••••••••"
                />
              </div>
            </div>
            {loginError && (
              <p className="text-sm text-red-600 mt-4" data-testid="admin-login-error">{loginError}</p>
            )}
            <button type="submit" disabled={loggingIn} className="btn-primary w-full py-3.5 mt-7" data-testid="admin-login-btn">
              {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : <LockKeyhole className="w-4 h-4" />}
              {loggingIn ? "Signing in..." : "Sign In"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  /* ---------------- Dashboard view ---------------- */
  return (
    <main className="min-h-screen bg-[#F8F7FB] pt-28 pb-24 px-4 sm:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Media Manager</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Events &amp; Media Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-view-site-btn" className="btn-outline px-5 py-2.5 text-sm">
              <ArrowLeft className="w-4 h-4" /> View Site
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="btn border border-slate-300 text-slate-700 hover:bg-slate-100 px-5 py-2.5 text-sm rounded-full">
              <LogOut className="w-4 h-4" /> Log Out
            </button>
          </div>
        </div>

        {/* Upload */}
        <form onSubmit={upload} className="bg-white border border-purple-100 rounded-3xl p-6 sm:p-8 mt-10 shadow-lg shadow-purple-100" data-testid="admin-upload-form">
          <h2 className="font-display text-xl font-bold text-slate-900 flex items-center gap-2.5">
            <UploadCloud className="w-5 h-5 text-purple-700" /> New Upload
          </h2>
          <div className="grid sm:grid-cols-2 gap-5 mt-6">
            <div>
              <label className="field-label" htmlFor="up-title">Title</label>
              <input
                id="up-title"
                className="field"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                required
                data-testid="admin-upload-title-input"
                placeholder="e.g. Summer League Finals"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="up-date">Event date</label>
              <input
                id="up-date"
                type="date"
                className="field"
                value={form.event_date}
                onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))}
                data-testid="admin-upload-date-input"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="field-label" htmlFor="up-desc">Description</label>
            <textarea
              id="up-desc"
              className="field min-h-[90px] resize-y"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              data-testid="admin-upload-desc-input"
              placeholder="Short caption for the gallery (optional)"
            />
          </div>
          <div className="mt-5">
            <label className="field-label" htmlFor="up-file">Photo or video</label>
            <input
              id="up-file"
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="admin-upload-file-input"
              className="w-full rounded-xl border-2 border-dashed border-purple-300 bg-lavender-50 px-4 py-3.5 text-sm text-slate-700 file:mr-4 file:rounded-full file:border-0 file:bg-purple-700 file:px-4 file:py-1.5 file:text-white file:text-xs file:font-semibold hover:file:bg-purple-600"
            />
            {file && (
              <p className="text-xs text-slate-500 mt-2" data-testid="admin-upload-file-chosen">
                {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB · {file.type.startsWith("video") ? "video" : "photo"}
              </p>
            )}
          </div>
          {uploading && (
            <div className="mt-5">
              <div className="h-1.5 rounded-full bg-purple-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-violet-600 to-fuchsia-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-2">Uploading {progress}%</p>
            </div>
          )}
          {uploadMsg && (
            <p className={`text-sm mt-4 ${uploadMsg.ok ? "text-emerald-600" : "text-red-600"}`} data-testid="admin-upload-message">
              {uploadMsg.text}
            </p>
          )}
          <button type="submit" disabled={uploading} className="btn-primary px-8 py-3 mt-6" data-testid="admin-upload-submit-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {uploading ? "Uploading..." : "Upload (stays draft until you publish)"}
          </button>
        </form>

        {/* Posts */}
        <h2 className="font-display text-xl font-bold text-slate-900 mt-14 mb-5">
          Your posts <span className="text-slate-400 font-sans font-normal text-sm">({posts.length})</span>
        </h2>
        {loadingPosts ? (
          <div className="flex items-center gap-3 text-slate-500 py-10" data-testid="admin-posts-loading">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" /> Loading posts...
          </div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-purple-200 bg-lavender-50 p-10 text-center text-slate-500 text-sm" data-testid="admin-posts-empty">
            No uploads yet. Your first photo or video goes above.
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-posts-list">
            {posts.map((post) => (
              <div key={post.id} className="bg-white border border-purple-100 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row gap-5 shadow-sm" data-testid={`admin-post-${post.id}`}>
                <div className="w-full sm:w-44 aspect-video rounded-xl overflow-hidden bg-slate-900 shrink-0">
                  {post.media_type === "video" ? (
                    <video src={mediaUrl(post.storage_path)} muted preload="metadata" className="w-full h-full object-cover" />
                  ) : (
                    <img src={mediaUrl(post.storage_path)} alt={post.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  {editingId === post.id ? (
                    <div className="space-y-3">
                      <input
                        className="field"
                        value={editDraft.title}
                        onChange={(e) => setEditDraft((d) => ({ ...d, title: e.target.value }))}
                        data-testid={`admin-edit-title-${post.id}`}
                      />
                      <input
                        type="date"
                        className="field"
                        value={editDraft.event_date}
                        onChange={(e) => setEditDraft((d) => ({ ...d, event_date: e.target.value }))}
                        data-testid={`admin-edit-date-${post.id}`}
                      />
                      <textarea
                        className="field min-h-[70px] resize-y"
                        value={editDraft.description}
                        onChange={(e) => setEditDraft((d) => ({ ...d, description: e.target.value }))}
                        placeholder="Caption"
                        data-testid={`admin-edit-desc-${post.id}`}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(post)} className="btn-primary px-5 py-2 text-xs" data-testid={`admin-save-${post.id}`}>
                          <Save className="w-3.5 h-3.5" /> Save
                        </button>
                        <button onClick={() => setEditingId(null)} className="btn border border-slate-300 text-slate-600 hover:bg-slate-100 px-5 py-2 text-xs" data-testid={`admin-cancel-${post.id}`}>
                          <X className="w-3.5 h-3.5" /> Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center gap-2.5">
                        <span className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] border ${
                          post.published
                            ? "bg-emerald-100 text-emerald-700 border-emerald-300"
                            : "bg-amber-100 text-amber-800 border-amber-300"
                        }`} data-testid={`admin-status-${post.id}`}>
                          {post.published ? "Published" : "Draft"}
                        </span>
                        <span className="rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] bg-purple-100 text-purple-800 border border-purple-200">
                          {post.media_type === "video" ? (
                            <span className="flex items-center gap-1.5"><VideoIcon className="w-3 h-3" /> Video</span>
                          ) : (
                            <span className="flex items-center gap-1.5"><ImageIcon className="w-3 h-3" /> Photo</span>
                          )}
                        </span>
                        {post.event_date && (
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                            {formatDate(post.event_date)}
                          </span>
                        )}
                      </div>
                      <h3 className="font-display font-bold text-slate-900 mt-2.5 truncate">{post.title}</h3>
                      {post.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{post.description}</p>}
                      <div className="flex flex-wrap gap-2 mt-4">
                        <button
                          onClick={() => togglePublish(post)}
                          className={`btn px-4 py-2 text-xs rounded-full border transition-colors ${
                            post.published
                              ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                              : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          }`}
                          data-testid={`admin-publish-toggle-${post.id}`}
                        >
                          {post.published ? <><EyeOff className="w-3.5 h-3.5" /> Unpublish</> : <><Eye className="w-3.5 h-3.5" /> Publish</>}
                        </button>
                        <button onClick={() => startEdit(post)} className="btn border border-slate-300 text-slate-700 hover:bg-slate-100 px-4 py-2 text-xs rounded-full" data-testid={`admin-edit-${post.id}`}>
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                        <button onClick={() => remove(post)} className="btn border border-red-300 text-red-600 hover:bg-red-50 px-4 py-2 text-xs rounded-full" data-testid={`admin-delete-${post.id}`}>
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default Admin;